// src/screens/AppointmentsScreen.js
import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { appointmentsAPI } from '../services/api';
import { useNavigate, Link } from 'react-router-dom';
import AppHeader from '../components/AppHeader';

const AppointmentsScreen = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'pending' | 'cancelled'
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const fetchAppointments = async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError('');
      const { data } = await appointmentsAPI.getAll();

      // sort all appointments by date/time DESC once
      const sorted = (data || []).slice().sort((a, b) => {
        const da = a.date ? new Date(a.date) : new Date(0);
        const db = b.date ? new Date(b.date) : new Date(0);
        const cmpDate = db - da; // desc
        if (cmpDate !== 0) return cmpDate;

        // if same day, sort by time string desc (simple but fine for HH:MM)
        if (a.time && b.time) {
          return b.time.localeCompare(a.time);
        }
        return 0;
      });

      setAppointments(sorted);
    } catch (err) {
      console.error('Error fetching appointments', err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to load appointments.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const isToday = (dateString) => {
    if (!dateString) return false;
    const d = new Date(dateString);
    const now = new Date();
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  };

  const isUpcoming = (dateString) => {
    if (!dateString) return false;
    const d = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    return d >= today;
  };

  const handleCancelAppointment = async (appointmentId, e) => {
    // prevent card click navigation
    if (e) e.stopPropagation();
    try {
      setUpdatingId(appointmentId);
      setError('');
      await appointmentsAPI.update(appointmentId, { status: 'Canceled' });
      await fetchAppointments();
    } catch (err) {
      console.error('Error cancelling appointment', err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to cancel appointment.';
      setError(msg);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleOpenDetail = (appointmentId) => {
    navigate(`/appointments/${appointmentId}`);
  };

  if (!user) {
    return null;
  }

  // 🔹 Filter by tab:
  // - all: everything EXCEPT Canceled and No Show
  // - pending: only Pending
  // - cancelled: only Canceled
  const filteredAppointments = appointments.filter((b) => {
    if (filterStatus === 'pending') return b.status === 'Pending';
    if (filterStatus === 'cancelled') return b.status === 'Canceled';
    // 'all'
    return b.status !== 'Canceled' && b.status !== 'No Show';
  });

  // 🔹 Group by dealer
  const groupsMap = filteredAppointments.reduce((acc, b) => {
    const key = b.dealer?._id || 'no-dealer';
    if (!acc[key]) {
      acc[key] = {
        dealer: b.dealer,
        appointments: [],
      };
    }
    acc[key].appointments.push(b);
    return acc;
  }, {});

  const dealerGroups = Object.values(groupsMap);

  const statusChipClasses = (status) => {
    switch (status) {
      case 'Confirmed':
        return 'bg-green-600/20 text-green-400';
      case 'Vehicle Ready':
        return 'bg-cyan-600/20 text-cyan-400';
      case 'Checked In':
        return 'bg-purple-600/20 text-purple-400';
      case 'Completed':
        return 'bg-blue-600/20 text-blue-400';
      case 'Canceled':
        return 'bg-red-600/20 text-red-400';
      case 'No Show':
        return 'bg-orange-600/20 text-orange-400';
      case 'Pending':
        return 'bg-yellow-600/20 text-yellow-400';
      default:
        return 'bg-app-bg-secondary-light dark:bg-app-bg-secondary text-app-text-secondary-light dark:text-app-text-secondary';
    }
  };

  return (
    <div className="flex flex-col h-full customer-friendly">
      {/* Header */}
      <AppHeader title="Your Test Drives" subtitle="Past adventures and upcoming ones!" />

      <div className="flex-1 overflow-y-auto p-4 pb-20 space-y-3 text-sm">
        {/* Status filter tabs */}
        <div className="bg-white dark:bg-app-bg-secondary rounded-2xl p-2 border border-app-border-light/50 dark:border-app-border flex text-xs gap-2 shadow-sm">
          <button
            type="button"
            onClick={() => setFilterStatus('all')}
            className={`flex-1 py-2.5 rounded-xl font-semibold transition-all duration-300 ${
              filterStatus === 'all'
                ? 'bg-gradient-to-r from-customer-teal to-customer-teal-hover text-white shadow-md'
                : 'bg-app-bg-light dark:bg-app-input text-app-text-secondary-light dark:text-app-text-secondary hover:bg-customer-teal-light'
            }`}
          >
            Active
          </button>
          <button
            type="button"
            onClick={() => setFilterStatus('pending')}
            className={`flex-1 py-2.5 rounded-xl font-semibold transition-all duration-300 ${
              filterStatus === 'pending'
                ? 'bg-gradient-to-r from-customer-yellow to-customer-yellow-hover text-white shadow-md'
                : 'bg-app-bg-light dark:bg-app-input text-app-text-secondary-light dark:text-app-text-secondary hover:bg-customer-yellow-light'
            }`}
          >
            Pending
          </button>
          <button
            type="button"
            onClick={() => setFilterStatus('cancelled')}
            className={`flex-1 py-2.5 rounded-xl font-semibold transition-all duration-300 ${
              filterStatus === 'cancelled'
                ? 'bg-gradient-to-r from-customer-accent to-customer-accent-hover text-white shadow-md'
                : 'bg-app-bg-light dark:bg-app-input text-app-text-secondary-light dark:text-app-text-secondary hover:bg-customer-accent-light'
            }`}
          >
            Canceled
          </button>
        </div>

        {error && (
          <div className="bg-customer-accent-light text-customer-accent px-4 py-3 rounded-2xl text-sm font-medium">
            {error}
          </div>
        )}

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-customer-teal border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary">Loading your adventures...</p>
          </div>
        )}

        {!loading && filteredAppointments.length === 0 && (
          <div className="text-center mt-10 bg-customer-teal-light/30 dark:bg-app-bg-secondary rounded-3xl p-8">
            <div className="text-4xl mb-3">🚗</div>
            <p className="text-lg font-medium text-app-text-light dark:text-app-text mb-2">No test drives here yet!</p>
            <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary mb-4">
              Ready to find your perfect ride?
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 rounded-2xl bg-gradient-to-r from-customer-teal to-customer-teal-hover text-white font-semibold shadow-lg shadow-customer-teal/30 transition-all duration-300 hover:scale-105"
            >
              Explore Cars
            </Link>
          </div>
        )}

        {/* 🔹 Grouped by dealer */}
        {dealerGroups.map((group, idx) => (
          <div
            key={group.dealer?._id || `dealer-${idx}`}
            className="bg-app-input-light dark:bg-app-input rounded-2xl p-3 border border-app-input-border-light dark:border-app-input-border space-y-2"
          >
            {/* Dealer header */}
            <div className="flex justify-between items-center mb-1">
              <div className="min-w-0">
                <div className="font-semibold text-xs truncate">
                  {group.dealer?.name || 'Dealer'}
                </div>
                <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary truncate">
                  {group.dealer?.address?.city &&
                    group.dealer?.address?.state && (
                      <>
                        {group.dealer.address.city},{' '}
                        {group.dealer.address.state}
                      </>
                    )}
                </div>
              </div>
            </div>

            {/* Appointments for this dealer (already globally sorted by date desc) */}
            <div className="space-y-2">
              {group.appointments.map((appointment) => {
                const today = isToday(appointment.date);
                const upcoming = isUpcoming(appointment.date);
                const canCancel =
                  upcoming &&
                  (appointment.status === 'Pending' ||
                    appointment.status === 'Confirmed');

                return (
                  <div
                    key={appointment._id}
                    onClick={() => handleOpenDetail(appointment._id)}
                    className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-xl p-3 border border-app-border-light dark:border-app-border space-y-2 cursor-pointer hover:border-app-accent/70 transition-colors"
                  >
                    {/* Status + date row */}
                    <div className="flex justify-between items-center gap-2">
                      <div className="text-[11px] text-app-text-light dark:text-app-text flex items-center gap-2">
                        <span>
                          {appointment.date
                            ? new Date(appointment.date).toLocaleDateString(
                                undefined,
                                {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                }
                              )
                            : ''}
                          {appointment.time ? ` • ${appointment.time}` : ''}
                        </span>
                        {today && (
                          <span className="text-[10px] px-2 py-1 rounded-full bg-app-accent/10 text-app-accent">
                            Today
                          </span>
                        )}
                        {upcoming && appointment.status === 'Pending' && (
                          <span className="text-[10px] px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-400">
                            Awaiting confirmation
                          </span>
                        )}
                      </div>
                      <span
                        className={`text-[10px] px-2 py-1 rounded-full ${statusChipClasses(
                          appointment.status
                        )}`}
                      >
                        {appointment.status}
                      </span>
                    </div>

                    {/* Vehicles summary */}
                    <div className="mt-1 space-y-1">
                      {appointment.vehicles?.map((v) => (
                        <div
                          key={v._id}
                          className="flex items-center gap-2 text-[11px]"
                        >
                          {v.image && (
                            <img
                              src={v.image}
                              alt={`${v.make} ${v.model}`}
                              className="w-10 h-8 rounded-md object-cover flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">
                              {v.year} {v.make} {v.model}
                            </div>
                            <div className="text-app-text-secondary-light dark:text-app-text-secondary truncate">
                              ${v.price.toLocaleString()} • {v.type}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Actions */}
                    {canCancel && (
                      <div className="mt-2">
                        <button
                          type="button"
                          disabled={updatingId === appointment._id}
                          onClick={(e) =>
                            handleCancelAppointment(appointment._id, e)
                          }
                          className="w-full py-2 rounded-xl text-[11px] font-semibold bg-red-600/80 text-black disabled:opacity-50"
                        >
                          {updatingId === appointment._id
                            ? 'Cancelling...'
                            : 'Cancel appointment'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AppointmentsScreen;
